import { HashLink } from 'react-router-hash-link';
import Alleles from './Alleles';
import Consequence from './Consequence';
import Position from './Position';
import Type from './Type';
import RiskAllele from './RiskAllele';
import GenomeExome from './Genome-Exome';

function AboutAttributesVariants() {
  return (
    <div id="section42">
      <h3>Variants</h3>
      <strong>
        <p>Variants in DISGENET are annotated with:</p>
      </strong>
      <ul>
        <Position />
        <Alleles />
        <Type />
        <GenomeExome />
        <Consequence />
        <li className="bullet-item">
          The gene corresponding to the consequence type assigned by VEP, or the one assigned by dbSNP
          database
        </li>
        <li className="bullet-item">
          The{' '}
          <a className="tt" target="_blank" rel="noreferrer" href="https://www.ensembl.org/">
            {' '}
            Ensembl
          </a>{' '}
          Gene identifier
        </li>
        <li className="bullet-item">
          The{' '}
          <HashLink smooth to="/About?section=metrics#specificity" className="js-fix-scroll">
            Disease Specificity Index
          </HashLink>{' '}
          (DSI)
        </li>
        <li className="bullet-item">
          The{' '}
          <HashLink smooth to="/About?section=metrics#pleiotropy" className="js-fix-scroll">
            Disease Pleiotropy Index{' '}
          </HashLink>{' '}
          (DPI)
        </li>
        <li className="bullet-item">The number of diseases associated to the variant (N diseases)</li>
        <RiskAllele />
        <li className="bullet-item">
          <strong> Pathogenicity scores SIFT and PolyPhen-2:</strong> For human variants that are predicted to
          result in an amino acid substitution, we provide the pathogenicity predictions from SIFT and
          PolyPhen-2 obtained from{' '}
          <a
            rel="noreferrer"
            target="_blank"
            href="https://www.ensembl.org/info/genome/variation/prediction/protein_function.html"
          >
            VEP
          </a>
          . For each variant, we provide the max value of Polyphen and the minimum value of SIFT.
          <li className="bullet-item">
            <a rel="noreferrer" target="_blank" href="http://sift.bii.a-star.edu.sg/">
              SIFT
            </a>{' '}
            predicts whether an amino acid substitution is likely to affect protein function based on sequence
            homology and the physicochemical similarity between the alternate amino acids. The score is the
            normalized probability that the amino acid change is tolerated; scores near zero are more likely
            to be deleterious. A qualitative prediction can be derived from this score such that substitutions
            with a score {'<'} 0.05 are called {'deleterious'} and all others are called
            {'tolerated'}.
          </li>
          <div className="table-responsive">
            <table
              className="table panther-table"
              style={{
                margin: '10px 50px 10px 50px',
              }}
            >
              <thead className="text-center">
                <td>
                  <strong>SIFT value</strong>
                </td>
                <td>
                  <strong>Qualitative prediction</strong>
                </td>
              </thead>
              <tbody className="text-center">
                <td>smaller than 0.05</td>
                <td>{'Deleterious'}</td>
              </tbody>
            </table>
          </div>
          <li className="bullet-item">
            <a rel="noreferrer" target="_blank" href="http://genetics.bwh.harvard.edu/pph2/">
              PolyPhen-2
            </a>{' '}
            predicts the effect of an amino acid substitution on the structure and function of a protein using
            sequence homology, Pfam annotations, 3D structures from PDB where available, and several other
            databases and tools (including DSSP, ncoils etc.). The PolyPhen score represents the probability
            that a substitution is damaging, so values nearer one are more confidently predicted to be
            deleterious (note that this is the opposite of SIFT). A qualitative classification can be derived
            from the PolyPhen score as follows:
          </li>
        </li>
        <div className="table-responsive">
          <table
            className="table panther-table"
            style={{
              margin: '10px 50px 10px 50px',
            }}
          >
            <thead className="text-center">
              <td>
                <strong>Polyphen value</strong>
              </td>
              <td>
                <strong>Qualitative prediction</strong>
              </td>
            </thead>
            <tbody className="text-center">
              <td>greater than 0.908</td>
              <td>"Probably Damaging"</td>
            </tbody>
            <tbody className="text-center">
              <td style={{ width: '500px' }}>greater than 0.446 and less than or equal to 0.908</td>
              <td>"Possibly Damaging"</td>
            </tbody>
            <tbody className="text-center">
              <td>less than or equal to 0.446</td>
              <td>"Benign"</td>
            </tbody>
            <tbody className="text-center">
              <td>Unknown</td>
              <td>Unknown</td>
            </tbody>
          </table>
        </div>
        {['test', 'development', 'dev'].includes(process.env.REACT_APP_ENVIRONMENT || '') ||
          (process.env.NODE_ENV !== 'production' && (
            <>
              <li className="bullet-item">
                HGVSp: Protein-level variant description based on a reference protein sequence.
              </li>
              <li className="bullet-item">
                HGVSc: Coding DNA-level variant description based on a protein-coding transcript reference
                sequence.
              </li>
            </>
          ))}
      </ul>
    </div>
  );
}

export default AboutAttributesVariants;
